import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { combineLatest, filter, Observable, startWith } from 'rxjs';
import { Store } from '@ngrx/store';
import { hasPersons, isTupCustomer } from '../../../core/states/offer/offer.selector';
import { Event, NavigationEnd, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { RoutePath } from '../../../routing/app-routing.module';
import { StepClickable } from '../../stepper-clickable/stepper-clickable.types';

export type ExplicitRoutePath =
  | Exclude<RoutePath, '' | 'basic-insurance/:personId'>
  | Exclude<RoutePath, '' | 'supplementary-insurance/:personId'>
  | 'root'
  | 'basic-insurance'
  | 'supplementary-insurance';

@Component({
  standalone: false,
  selector: 'ssc-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public hasBasket = false;
  @Input() public activePersonId$?: Observable<number | null>;
  @Input() public hasStepper = false;

  public hasPersons$: Observable<boolean>;
  public steps$: Observable<StepClickable[]>;

  @ViewChild('serviceNav') private serviceNavRef!: ElementRef;
  private resizeObserver!: ResizeObserver;
  public headerHeight: number;

  constructor(
    public router: Router,
    private store: Store,
    private translateService: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.hasPersons$ = this.store.select(hasPersons);

    this.steps$ = combineLatest([
      this.store.select(isTupCustomer),
      this.router.events.pipe(
        startWith(new NavigationEnd(0, '/', this.router.url)),
        filter(HeaderComponent.isNavigationEndEvent),
        map(HeaderComponent.getRoutePathFromUrl),
      ),
    ]).pipe(map(([tupCustomer, path]) => this.mapSteps(path, tupCustomer)));
  }

  public ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        this.headerHeight = entry.contentRect.height;
      }
    });

    this.resizeObserver.observe(this.serviceNavRef.nativeElement);
  }

  public ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private mapSteps(path: ExplicitRoutePath, tupCustomer: boolean): StepClickable[] {
    let steps: StepClickable[] = [];
    let stepNumber = 1;
    steps.push({
      step: stepNumber++,
      description: this.translateService.instant('STEPPER.PERSONS'),
      active: 'root' === path || 'tup' === path,
      route: '',
    });

    if (!tupCustomer) {
      steps.push({
        step: stepNumber++,
        description: this.translateService.instant('STEPPER.BASIC_INSURANCE'),
        active: 'basic-insurance' === path,
        route: '/basic-insurance/0',
      });
    }

    steps.push({
      step: stepNumber++,
      description: this.translateService.instant('STEPPER.SUPPLEMENTARY_INSURANCE'),
      active: 'supplementary-insurance' === path,
      route: '/supplementary-insurance/0',
    });

    steps.push({
      step: stepNumber++,
      description: this.translateService.instant('STEPPER.CHECK_OFFER'),
      active: 'summary' === path,
      route: '/summary',
    });

    return steps;
  }

  private static getRoutePathFromUrl({ urlAfterRedirects }: NavigationEnd): ExplicitRoutePath {
    if (urlAfterRedirects === '/') {
      return 'root';
    }
    // url has format /route/subroute
    return urlAfterRedirects.split('/')[1] as ExplicitRoutePath;
  }

  private static isNavigationEndEvent(event: Event): event is NavigationEnd {
    return event instanceof NavigationEnd;
  }
}
